import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { CardFormFields, emptyCardForm } from './CardFormFields';
import type { CardFormData } from './CardFormFields';

interface AddPaymentMethodDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    nickname: string;
    holderName: string;
    number: string;
    expiryMonth: number;
    expiryYear: number;
    cvv: string;
  }) => void;
  isLoading?: boolean;
}

/**
 * Modal de cadastro de cartão — Modal shared (F72: chrome padrão + bottom-sheet mobile).
 */
export const AddPaymentMethodDialog: React.FC<AddPaymentMethodDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [card, setCard] = useState<CardFormData>({ ...emptyCardForm });
  const prevOpen = useRef(false);

  // Reset form only when dialog opens (not on error/re-render)
  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      setCard({ ...emptyCardForm });
    }
    prevOpen.current = isOpen;
  }, [isOpen]);

  const canSubmit =
    card.holderName.trim() &&
    card.number.length === 16 &&
    card.expiryMonth.length === 2 &&
    card.expiryYear.length === 2 &&
    card.cvv.length === 3;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
    onSubmit({
      nickname: card.nickname || card.holderName,
      holderName: card.holderName,
      number: card.number,
      expiryMonth: parseInt(card.expiryMonth, 10),
      expiryYear: currentCentury + parseInt(card.expiryYear, 10),
      cvv: card.cvv,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <div data-testid="add-payment-method-dialog">
        <ModalHeader title="Adicionar Cartão" onClose={isLoading ? undefined : onClose}>
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#ede9fe' }}
          >
            <Icon name="credit-card" className="h-5 w-5" style={{ color: '#8b5cf6' }} />
          </div>
        </ModalHeader>

        <ModalContent className="px-6 py-5" padding={false}>
          <CardFormFields card={card} onChange={setCard} showNickname />
        </ModalContent>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            data-testid="add-payment-method-cancel"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            loading={isLoading}
            data-testid="add-payment-method-submit"
          >
            Adicionar
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

AddPaymentMethodDialog.displayName = 'AddPaymentMethodDialog';
